/**
 * layout - the matter workspace's binding of the shared detail-page surface.
 *
 * A matter has seven panes over its canvas:
 *
 *   assistant   a conversation about this matter               (right)
 *   context     the matter's facts and its economics snapshot  (left)
 *   connected   research sessions, drafts and insights         (right)
 *   access      who can see this matter, and the way to change  (right)
 *   activity    the audit log                                   (right)
 *   reader      a document or source, opened from a tab        (right)
 *   detail      the shared editor for whichever row is picked  (right)
 *
 * The centre is not a panel. It is the CANVAS - the tab bar and whichever tab
 * is open - always present, never tiled, with the panes floating over it.
 *
 * This file is now CONFIG ONLY. The shell, the hook, the rail and the storage
 * all come from `shared/surface` - see `defineDetailSurface`. What used to be
 * particular to matters is one line of it: `scope: 'entity'`.
 *
 * WHY ENTITY SCOPE. A litigation file worked from its documents and a
 * corporate file worked from its economics want different panes open, and one
 * shared key would make every matter inherit the arrangement of whichever was
 * opened last. So the matter id is part of the storage key.
 */

import {
  DockSide,
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
  PanelSurface,
} from '../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../shared/surface/defineDetailSurface';
import { isAiUsageEnabled } from '../../../config/featureFlags';

export type { DockSide };

export type MatterPanelId =
  | 'assistant'
  | 'context'
  | 'connected'
  | 'access'
  | 'activity'
  | 'aiUsage'
  | 'reader'
  | 'detail';
/**
 * The canvas's tabs, in bar order.
 *
 * The VALUES only: the page owns the labels (they need `t()`, an icon, a
 * tooltip, and Economics is behind a flag). Declaring them here is what lets
 * the hook check a `?tab=` off the URL and ignore one it does not know —
 * which is how a stale link stops landing on a blank canvas.
 *
 * `Economics` stays in the vocabulary even while the flag can hide its pill:
 * a matter's URL is allowed to name it, and the bar decides what to show.
 */
export type MatterTab =
  | 'Details'
  | 'Documents'
  | 'Q&As'
  | 'Tasks'
  | 'Notes'
  | 'Economics';

export const MATTER_TABS: MatterTab[] = [
  'Details',
  'Documents',
  'Q&As',
  'Tasks',
  'Notes',
  'Economics',
];

export type MatterPanelSlot = GenericSlot<MatterPanelId>;
export type MatterPanelLayout = GenericLayout<MatterPanelId>;

/* AI usage is dropped from the surface where the feature is not released —
   no slot, no rail pill, no persisted visibility, so a pane can never open
   onto it. See `config/featureFlags.isAiUsageEnabled`. */
export const MATTER_PANEL_IDS: MatterPanelId[] = [
  'assistant',
  'context',
  'connected',
  'access',
  'activity',
  ...(isAiUsageEnabled() ? (['aiUsage'] as MatterPanelId[]) : []),
  'reader',
  'detail',
];

/**
 * Every pane, in one place: its label, its resize limits, where it starts and
 * whether it gets a rail toggle.
 *
 * RESIZE LIMITS. The reference panes used to cap around 420–440px, which was
 * sized for glanceable facts and made them impossible to widen when someone
 * wanted to actually work in one. They now stretch to 720 - the reader and
 * the assistant, which carry a document and a conversation, go to 900. Floors
 * stay where they were: below ~240 a pane stops being readable.
 */
const MATTER_PANES: Record<MatterPanelId, DetailPaneConfig> = {
  /*
    The assistant docks RIGHT and leads that stack: a conversation belongs
    beside the work, on the side the reader already uses, and it is the pane
    most likely to be open alongside the canvas. Closed by default - it is
    opened deliberately, from the rail.
  */
  assistant: {
    label: 'Assistant',
    bounds: { min: 360, max: 900 },
    dock: 'right', index: 0, width: 420, visible: false, rail: true,
  },
  // Context is open by default: the facts of the matter are what every tab is
  // read against.
  context: {
    label: 'Context',
    bounds: { min: 240, max: 720 },
    dock: 'left', index: 0, width: 300, visible: true, rail: true,
  },
  connected: {
    label: 'Connected',
    bounds: { min: 240, max: 720 },
    dock: 'right', index: 1, width: 300, visible: false, rail: true,
  },
  /*
    ACCESS IS ITS OWN PANE. It used to be a collaborator list stapled to the
    top of the activity pane, on the theory that "who changed this" and "who
    can change this" are read together. They are not: one is a log you scroll
    and the other is a roster you check, the roster was squeezed into a
    non-scrolling header above a scroller, and there was no way to see the
    people without also loading the audit log. Two panes, one rail pill each.
  */
  access: {
    label: 'Access',
    bounds: { min: 240, max: 640 },
    dock: 'right', index: 2, width: 300, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 260, max: 720 },
    dock: 'right', index: 3, width: 320, visible: false, rail: true,
  },
  /*
    AI USAGE - what this matter has spent on AI, and on which kind of run.
    A matter's full analysis and every "generate more" top-up are separate
    runs against the same file, so the per-run list is the only place the
    shape of that spend is visible.

    A PANE, NOT A TAB, even though this is the one surface of the four that
    HAS content tabs. The canvas tabs are the matter's substance - its
    documents, its questions, its tasks - and usage is not substance, it is
    reference read against whatever tab is open, exactly like Activity and
    Access. Making it a tab here and a pane on the other three would also
    give the same readout two different homes.
  */
  aiUsage: {
    label: 'AI usage',
    bounds: { min: 300, max: 720 },
    dock: 'right', index: 4, width: 360, visible: false, rail: true,
  },
  // The reader carries a document, so it is allowed far more room than a
  // reference pane - and needs a floor wide enough for a page of text. No
  // rail toggle: it opens because a document was opened.
  reader: {
    label: 'Reader',
    bounds: { min: 320, max: 900 },
    dock: 'right', index: 5, width: 560, visible: false,
  },
  /*
    The DETAIL container: one shared editor for whichever list row is
    selected - a question, a task, a note. It is a pane like any other, so it
    docks, drags, resizes and persists with the rest, but it has NO rail
    toggle: it opens because something was selected, and closing it clears
    the selection. Wide bounds because a task's full field set needs them.
  */
  detail: {
    label: 'Details',
    bounds: { min: 380, max: 900 },
    dock: 'right', index: 6, width: 460, visible: false,
  },
};

/** The unscoped key layouts used to live under, migrated once per user. */
export const MATTER_LEGACY_KEY_PREFIX = 'matter:layout:v1:';

export const MATTER_SURFACE = defineDetailSurface<MatterPanelId, MatterTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `matter-surface`, etc.
  name: 'matter',
  scope: 'entity',
  ids: MATTER_PANEL_IDS,
  panes: MATTER_PANES,
  tabs: MATTER_TABS,
  detailPane: 'detail',
  readerPane: 'reader',
  legacyKeys: [{ prefix: MATTER_LEGACY_KEY_PREFIX }],
});

/* ── derived, for call sites and tests that want the plain records ── */

export const MATTER_PANEL_LABELS: Record<MatterPanelId, string> = MATTER_SURFACE.labels;
export const MATTER_PANEL_BOUNDS: Record<MatterPanelId, PanelBounds> = MATTER_SURFACE.bounds;
export const MATTER_DEFAULT_LAYOUT: MatterPanelLayout = MATTER_SURFACE.defaults;

/** The pre-scoping key. Still the migration's source; nothing writes it. */
export const matterLayoutStorageKey = (matterId: string): string =>
  `${MATTER_LEGACY_KEY_PREFIX}${matterId}`;

/**
 * The `PanelSurface` for one matter, keyed the OLD unscoped way.
 *
 * Kept because the pure-function tests operate on a surface object and the
 * migration reads through the same key. Runtime persistence goes through
 * `MATTER_SURFACE.surfaceFor(userId, matterId)` instead - see
 * `useDetailSurface`.
 */
export function matterSurface(matterId: string): PanelSurface<MatterPanelId> {
  return MATTER_SURFACE.legacySurface(matterId || 'unknown');
}
